import { CommonActions, OrderActions, ScheduleActions } from '@Actions/index';
import { ISlot } from '@Reducers/schedule';
import { useAppDispatch } from '@Redux/hooks';
import { ScheduleService } from '@Services';
import { UpdateConsultationPayload } from '@Services/Schedule/schedule-payload';
import { CONSULTATION_STATE, RESCHEDULE_REASON, SLOT_SELECTION } from '@Utils/constants';
import { LOG_ERROR } from '@Utils/logger';
import { useCallback, useRef, useState } from 'react';

export interface IUpdateConsultationOrder {
  selectedSlot: ISlot;
  consultationId: string;
  locationId: string;
  appointmentState: string;
  selectedLanguages: string[];
  appointmentMode?: string;
  trackSlots?: (eventName: string) => void;
  version: 1 | 2;
}

const useScheduleAppointment = () => {
  const dispatch = useAppDispatch();
  const [isUpdatingAppointment, setIsUpdatingAppointment] = useState(false);
  const isUpdatingAppointmentRef = useRef(null);
  isUpdatingAppointmentRef.current = isUpdatingAppointment;

  const scheduleAppointment = useCallback(
    (payload: IUpdateConsultationOrder) => {
      const updatePayload: UpdateConsultationPayload = {
        consultationId: payload.consultationId,
        locationId: payload.locationId,
        blockId: payload.selectedSlot?.blockId,
        startTime: payload.selectedSlot?.startDate,
        consultationState:
          payload.version !== 2 && payload.appointmentState === CONSULTATION_STATE.RESCHEDULE
            ? CONSULTATION_STATE.SCHEDULE
            : payload.appointmentState,
        preferredLanguages: payload.selectedLanguages,
        reason: payload.appointmentState === CONSULTATION_STATE.RESCHEDULE ? RESCHEDULE_REASON : undefined,
        appointmentMode: payload.appointmentMode,
      };
      new Promise(async (resolve, reject) => {
        try {
          setIsUpdatingAppointment(true);
          payload?.trackSlots?.(SLOT_SELECTION.SLOT_CONFIRMED);
          const result = await ScheduleService.UpdateConsultation(updatePayload);
          if (result?.status === 201 && result?.data?.status === 'confirmed') resolve(result.data);
          else reject(result);
        } catch (error) {
          reject(error);
        }
      })
        .then((result: any) => {
          dispatch(
            CommonActions.UpdateQueryData({
              date: result?.appointment?.startTime,
              appointment_id: result?.appointment?.id,
            }),
          );
          dispatch(
            ScheduleActions.UpdateConsultationState({
              appointmentId: result?.appointment?.id,
              date: result?.appointment?.startTime,
            }),
          );
          dispatch(OrderActions.UpdateOrderConfirmed(true));
          setIsUpdatingAppointment(false);
        })
        .catch((error) => {
          LOG_ERROR('Error: useScheduleAppointment', error);
          setIsUpdatingAppointment(false);
        });
    },
    [dispatch],
  );

  return {
    scheduleAppointment,
    isUpdatingAppointment: isUpdatingAppointmentRef,
  };
};

export default useScheduleAppointment;
